import { useCallback, useEffect, useRef, useState } from 'react';
import type { UploadAccess } from '../shared/photo';
import { api } from './api';

export function useUploadAccess(pollSeconds: number) {
  const [access, setAccess] = useState<UploadAccess | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const deadline = useRef(0);
  const generation = useRef(0);
  const accept = useCallback((next: UploadAccess) => {
    generation.current++;
    const remaining = next.bannedUntil
      ? Math.max(0, Date.parse(next.bannedUntil) - Date.parse(next.serverNow))
      : 0;
    deadline.current = performance.now() + remaining;
    setSeconds(Math.ceil(remaining / 1000));
    setAccess(next);
    setError('');
  }, []);

  useEffect(() => {
    let stopped = false;
    let fetching = false;
    const refresh = async () => {
      if (fetching || document.visibilityState === 'hidden') return;
      fetching = true;
      const version = generation.current;
      try {
        const next = await api.uploadAccess();
        if (!stopped && version === generation.current) accept(next);
      } catch {
        if (!stopped) setError('Unable to check photo access. Reconnect to try again.');
      } finally {
        fetching = false;
      }
    };
    void refresh();
    const poll = setInterval(() => void refresh(), pollSeconds * 1000);
    const tick = setInterval(
      () => setSeconds(Math.max(0, Math.ceil((deadline.current - performance.now()) / 1000))),
      1000,
    );
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      stopped = true;
      clearInterval(poll);
      clearInterval(tick);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [accept, pollSeconds]);

  return {
    banned: Boolean(access?.bannedUntil),
    paused: Boolean(access?.uploadsPaused),
    familyBlocked: !access || Boolean(access.bannedUntil),
    blocked: !access || Boolean(access.bannedUntil) || Boolean(access.uploadsPaused),
    seconds,
    error,
    accept,
  };
}
